import { useCallback, useEffect, useState } from 'react';
import navScrollTo from '../components/ScrollerNav/navScrollTo';
import type { NavItem } from '../components/ScrollerNav/types';
import { isValidTabId } from '../utils/isValidTabId';

export default function useScrollerNavNavigation(items: NavItem[]) {
  const [selectedTab, setSelectedTab] = useState(items[0].id);
  const [isScrollingToSection, setIsScrollingToSection] = useState(false);

  useEffect(() => {
    const derivedTabId = window.location.hash.slice(1);
    if (isValidTabId(derivedTabId, items)) {
      setSelectedTab(derivedTabId);
    }
  }, [items]);

  const onTabChange = useCallback((id: string) => {
    setSelectedTab(id);
    setIsScrollingToSection(true);
    navScrollTo(id);
  }, []);

  const onSectionScrolledIntoView = useCallback(
    (id: string | null) => {
      // We're scrolling to a specific section, from clicking a tab, but we're not there yet. Ignore any scroll-based changes for now.
      if (isScrollingToSection && id !== selectedTab) {
        return;
      }

      // We scrolled away from the scroller nav completely.
      if (id === null) {
        window.history.replaceState(null, '', ' ');
        return;
      }

      // Not every section we render has a corresponding tab for navigation. Don't change the selected tab based on the latest scroll event.
      if (!isValidTabId(id, items)) {
        return;
      }

      setIsScrollingToSection(false);
      setSelectedTab(id);
      window.history.replaceState(null, '', `#${id}`);
    },
    [selectedTab, isScrollingToSection, items]
  );

  return {
    onTabChange,
    onSectionScrolledIntoView,
    selectedTab
  };
}
